import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Box,
	Chip,
	Collapse,
	IconButton,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Fragment, useEffect, useRef, useState } from "react";
import { SHOW_IN_PLC } from "../../straightener-common/constants";
import type {
	ProductNode,
	StraightenerRecipeVersion,
} from "../models/StraightenerRecipe";

// The recipe's identity is its name + crew; the version number is secondary
// metadata shown de-emphasized (light gray) at the end of the row.
const recipeLabel = (r: StraightenerRecipeVersion): string => {
	const name = r.Name ?? "";
	const crew = r.Crew ? ` · Crew ${r.Crew}` : "";
	return `${name}${crew}`;
};

// CreatedAt is stored/returned in UTC; display it in the server's local time.
const fmtDate = (v: string | null): string =>
	v ? dxtToLocalServerTime(v, "yyyy-MM-dd HH:mm") : "";

const plcChip = (
	<Chip
		size="small"
		label="IN PLC"
		color="success"
		sx={{ height: 18, fontSize: "0.65rem", "& .MuiChip-label": { px: 0.7 } }}
	/>
);

interface Props {
	nodes: ProductNode[];
	selectedProductId: number | null;
	selectedRecipeId: number | null;
	onSelectProduct: (node: ProductNode) => void;
	onSelectVersion: (version: StraightenerRecipeVersion) => void;
	// When provided, each version row shows a trash icon (soft delete).
	onRemoveVersion?: (version: StraightenerRecipeVersion) => void;
	// When provided, each version row shows a duplicate icon (copy modal).
	onDuplicateVersion?: (version: StraightenerRecipeVersion) => void;
	// When provided, the product context menu offers "Create recipe".
	onCreateRecipe?: (node: ProductNode) => void;
}

// Product-first tree, compact single-line rows (styled after the straightener
// recipe mockup): each product row has an expand chevron when it has at least
// one version; the current version is marked with a green Current chip.
export const StraightenerProductsTree = ({
	nodes,
	selectedProductId,
	selectedRecipeId,
	onSelectProduct,
	onSelectVersion,
	onRemoveVersion,
	onDuplicateVersion,
	onCreateRecipe,
}: Props) => {
	const [expanded, setExpanded] = useState<Set<number>>(new Set());
	// Right-click context menu: recipe rows offer Duplicate / Delete, product
	// rows offer Create recipe (same handlers as the inline icons/buttons).
	const [ctxMenu, setCtxMenu] = useState<{
		x: number;
		y: number;
		version?: StraightenerRecipeVersion;
		node?: ProductNode;
	} | null>(null);
	const openCtxMenu = (
		e: React.MouseEvent,
		target: { version?: StraightenerRecipeVersion; node?: ProductNode },
	) => {
		e.preventDefault();
		e.stopPropagation();
		setCtxMenu({ x: e.clientX + 2, y: e.clientY - 6, ...target });
	};
	const selectedRowRef = useRef<HTMLDivElement>(null);

	const toggle = (id: number) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	// When a recipe is selected (e.g. a freshly created version), expand its
	// product so the row becomes visible.
	useEffect(() => {
		if (selectedRecipeId == null) return;
		const node = nodes.find((n) =>
			n.versions.some((v) => v.Id === selectedRecipeId),
		);
		if (node && !expanded.has(node.productSizeId)) {
			setExpanded((prev) => new Set(prev).add(node.productSizeId));
		}
	}, [selectedRecipeId, nodes, expanded]);

	// Scroll the selected row into view once it is rendered (after expansion).
	useEffect(() => {
		selectedRowRef.current?.scrollIntoView({ block: "nearest" });
	}, [selectedRecipeId, expanded]);

	return (
		<>
			<List dense disablePadding sx={{ height: "100%", overflow: "auto" }}>
				{nodes.map((node) => {
					const hasVersions = node.versions.length > 0;
					const isExpanded = expanded.has(node.productSizeId);
					const productSelected =
						selectedProductId === node.productSizeId && !selectedRecipeId;
					const plcHere = node.versions.some((v) => v.IsPlcCurrent);

					return (
						<Fragment key={node.productSizeId}>
							<ListItemButton
								selected={productSelected}
								onClick={() => onSelectProduct(node)}
								onContextMenu={
									onCreateRecipe
										? (e) => {
												if (!productSelected) onSelectProduct(node);
												openCtxMenu(e, { node });
											}
										: undefined
								}
								sx={{
									py: 0.6,
									minHeight: 0,
									gap: 0.5,
									borderBottom: "1px solid",
									borderColor: "divider",
									"&.Mui-selected": {
										backgroundColor: (t) => alpha(t.palette.primary.main, 0.22),
									},
									"&.Mui-selected:hover": {
										backgroundColor: (t) => alpha(t.palette.primary.main, 0.3),
									},
								}}
							>
								{hasVersions ? (
									<IconButton
										size="small"
										sx={{ p: 0, color: "text.secondary" }}
										onClick={(e) => {
											e.stopPropagation();
											toggle(node.productSizeId);
										}}
									>
										{isExpanded ? (
											<ExpandMoreIcon fontSize="small" />
										) : (
											<ChevronRightIcon fontSize="small" />
										)}
									</IconButton>
								) : (
									<Box sx={{ width: 20, flexShrink: 0 }} />
								)}
								<Typography
									noWrap
									sx={{ fontWeight: 600, fontSize: "0.95rem", flexShrink: 0 }}
								>
									({node.productTypeName}) {node.productName}
								</Typography>
								<Typography
									noWrap
									sx={{
										fontSize: "0.85rem",
										color: node.current ? "text.secondary" : "text.disabled",
										fontStyle: node.current ? "normal" : "italic",
										flex: 1,
										minWidth: 0,
									}}
								>
									{node.current ? (
										<>
											{`· Default: ${recipeLabel(node.current)}`}
											<Box
												component="span"
												sx={{ color: "text.disabled", ml: 0.5 }}
											>
												{`· v${node.current.Version}`}
											</Box>
										</>
									) : null}
								</Typography>
								{SHOW_IN_PLC && plcHere ? plcChip : null}
							</ListItemButton>

							{hasVersions ? (
								<Collapse in={isExpanded} timeout="auto" unmountOnExit>
									<List
										dense
										disablePadding
										sx={{ bgcolor: "background.paper" }}
									>
										{node.versions.map((v) => (
											<ListItemButton
												key={v.Id}
												ref={
													selectedRecipeId === v.Id ? selectedRowRef : undefined
												}
												selected={selectedRecipeId === v.Id}
												onClick={() => onSelectVersion(v)}
												onContextMenu={
													onDuplicateVersion || onRemoveVersion
														? (e) => {
																if (selectedRecipeId !== v.Id)
																	onSelectVersion(v);
																openCtxMenu(e, { version: v });
															}
														: undefined
												}
												sx={{
													pl: 6,
													py: 0.5,
													minHeight: 0,
													gap: 0.5,
													"&.Mui-selected": {
														backgroundColor: (t) =>
															alpha(t.palette.primary.main, 0.22),
													},
													"&.Mui-selected:hover": {
														backgroundColor: (t) =>
															alpha(t.palette.primary.main, 0.3),
													},
												}}
											>
												<Typography
													noWrap
													sx={{
														fontSize: "0.9rem",
														color: v.IsActive
															? "text.primary"
															: "text.disabled",
													}}
												>
													{recipeLabel(v)}
												</Typography>
												{v.IsCurrent ? (
													<Chip
														size="small"
														label="Default"
														color="success"
														sx={{ height: 18, fontSize: "0.65rem" }}
													/>
												) : null}
												{SHOW_IN_PLC && v.IsPlcCurrent ? plcChip : null}
												{v.IsDraft ? (
													<Chip
														size="small"
														label="Draft"
														color="warning"
														sx={{ height: 18, fontSize: "0.65rem" }}
													/>
												) : !v.IsActive ? (
													<Chip
														size="small"
														label="Inactive"
														sx={{ height: 18, fontSize: "0.65rem" }}
													/>
												) : null}
												<Box sx={{ flex: 1, minWidth: 8 }} />
												<Typography
													noWrap
													sx={{ fontSize: "0.78rem", color: "text.disabled" }}
												>
													v{v.Version} · {v.CreatedBy ?? ""} ·{" "}
													{fmtDate(v.CreatedAt)}
												</Typography>
												{onDuplicateVersion ? (
													<Tooltip title="Duplicate">
														<IconButton
															size="small"
															aria-label="Duplicate recipe"
															onClick={(e) => {
																e.stopPropagation();
																onDuplicateVersion(v);
															}}
															sx={{ p: 0.25, color: "text.secondary" }}
														>
															<ContentCopyIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												) : null}
												{onRemoveVersion ? (
													<Tooltip title="Delete">
														<IconButton
															size="small"
															color="error"
															aria-label="Delete recipe"
															onClick={(e) => {
																e.stopPropagation();
																onRemoveVersion(v);
															}}
															sx={{ p: 0.25 }}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												) : null}
											</ListItemButton>
										))}
									</List>
								</Collapse>
							) : null}
						</Fragment>
					);
				})}
			</List>

			<Menu
				open={ctxMenu != null}
				onClose={() => setCtxMenu(null)}
				anchorReference="anchorPosition"
				anchorPosition={
					ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined
				}
				slotProps={{
					// A second right-click lands on the menu backdrop: keep the
					// browser menu out and just close ours.
					root: {
						onContextMenu: (e: React.MouseEvent) => {
							e.preventDefault();
							setCtxMenu(null);
						},
					},
				}}
			>
				{ctxMenu?.version && onDuplicateVersion ? (
					<MenuItem
						onClick={() => {
							const v = ctxMenu.version;
							setCtxMenu(null);
							if (v) onDuplicateVersion(v);
						}}
					>
						<ListItemIcon>
							<ContentCopyIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>Duplicate</ListItemText>
					</MenuItem>
				) : null}
				{ctxMenu?.version && onRemoveVersion ? (
					<MenuItem
						onClick={() => {
							const v = ctxMenu.version;
							setCtxMenu(null);
							if (v) onRemoveVersion(v);
						}}
					>
						<ListItemIcon>
							<DeleteIcon fontSize="small" color="error" />
						</ListItemIcon>
						<ListItemText>Delete</ListItemText>
					</MenuItem>
				) : null}
				{ctxMenu?.node && onCreateRecipe ? (
					<MenuItem
						onClick={() => {
							const n = ctxMenu.node;
							setCtxMenu(null);
							if (n) onCreateRecipe(n);
						}}
					>
						<ListItemIcon>
							<AddIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText>Create recipe</ListItemText>
					</MenuItem>
				) : null}
			</Menu>
		</>
	);
};
